<template>
    <div class="container admin-table-page">
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select
                        id="report-select"
                        v-model="selReporte"
                        @change="onReportChange"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option v-for="reporte in listReporte" :key="reporte.id" :value="String(reporte.codigo)">
                            {{ reporte?.nombre }}
                        </option>
                    </b-form-select>
                    
                    <b-form-select
                        id="validity-select"
                        v-model="selVigencia"
                        @change="listPlanConsoli = []; fgl_consulta = false"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option v-for="vigencia in listVigencia" :key="vigencia.vigencia" :value="vigencia.vigencia">
                            {{ vigencia?.vigencia }}
                        </option>
                    </b-form-select>
                    
                    <b-button
                        class="consult-btn ml-2"
                        @click="consultaNiif()"
                        :disabled="!selReporte || !selVigencia"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <div class="admin-table-block mt-4">
            <!-- ESTADO INICIAL - Sin datos -->
            <div v-if="!fgl_consulta && !isLoading && listPlanConsoli.length === 0">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">
                                {{ labels.selectFilters }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- LOADING -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <!-- TABLA CON DATOS -->
            <div v-if="!isLoading && fgl_consulta && listPlanConsoli.length > 0">
                <CorporateDataTable
                    :items="listPlanConsoli"
                    :fields="allFields"
                    :labels="tableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="500"
                    :table-max-height="500"
                    :show-search="showSearchTable"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefs"
                    :toolbar-disabled="!showToolbarExtras"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="crearNiif"
                                :disabled="!showToolbarExtras || isVigenciaBloqueada || !selReporte || !selVigencia"
                            >
                                {{ labels.create }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(niif)="data">
                        <div class="text-center"><strong>{{ data.item?.niif }}</strong></div>
                    </template>

                    <template #cell(nameNiif)="data">
                        <div class="text-left">{{ data.item?.nameNiif }}</div>
                    </template>

                    <template #cell(namePrimerNivel)="data">
                        <div class="text-left">{{ data.item?.namePrimerNivel }}</div>
                    </template>

                    <template #cell(nameSegundoNivel)="data">
                        <div class="text-left">{{ data.item?.nameSegundoNivel }}</div>
                    </template>

                    <template #cell(accionEdit)="data">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px;"
                            class="md-fab md-primary table-action-btn"
                            @click.stop="editaNiif(data.item.id)"
                            :disabled="isVigenciaBloqueada"
                        >
                            <i><font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/></i>
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="data">
                        <b-button
                            class="reset-btn-danger table-action-btn"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center;"
                            @click.stop="deleteNiif(data.item.id)"
                            :disabled="isVigenciaBloqueada"
                        >
                            <i><font-awesome-icon :icon="['fas', 'trash']" size="xs"/></i>
                        </b-button>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA VACÍA - Consulta realizada sin resultados -->
            <div v-if="!isLoading && fgl_consulta && listPlanConsoli.length === 0" class="text-center py-5">
                <font-awesome-icon
                    :icon="['fas', 'table']"
                    size="3x"
                    class="text-muted mb-3"
                    style="opacity: 0.3;"
                />
                <p class="h4 text-muted">
                    {{ labels.noData }}
                </p>
                <p class="text-muted">
                    {{ labels.noResults }}
                </p>
            </div>
        </div>

        <Crear
            :showDialogCrearNiif="showDialogCrearNiif"
            :vigencia="vigenciaSelected"
            :codReporte="selReporte"
            :listFirstLevel="listFirstLevel"
            @clicked="closeModal"
            @reload="refrescaTabla"
        />

        <Edit
            :showDialogEditNiif="showDialogEditNiif"
            @clicked="closeModal"
            :Item="itemSelected"
            :listFirstLevel="listFirstLevel"
            :listSecondLevelFirst="listSecondLevelFirst"
            @leaSegundoNivel="leaSegundoNivel"
            :listNiif="listPlanConsoli"
            @reload="refrescaTabla"
            @delete-item="deleteNiif"
        />

        <InacticaItem
            :InactivaItem="showModDel"
            @confirmDelete="confirmDel"
            @cierraModal="closeModal"
        />
    </div>
</template>

<script>
import PlanConsolidacion from '../../services/PlanConsolidacion'
import TipoReporte from '../../services/TipoReporte'
import PrimerNivel from '../../services/PrimerNivel'
import SegundoNivel from '../../services/SegundoNivel'
import InacticaItem from '../utils/InacticaItem.vue'
import Crear from './Crear.vue'
import Edit from './Edit.vue'
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: "Niif",
    components: {
        Edit,
        Crear,
        InacticaItem,
        CorporateDataTable
    },
    data: () => ({
        fgl_consulta: false,
        companyId: "",
        selVigencia: "",
        selVigenciaAnterior: "",
        searchNiif: null,
        listPlanConsoli: [],
        listPlanConsoliOrigen: [],
        showDialogCrearNiif: false,
        showDialogEditNiif: false,
        listFirstLevel: [],
        listSecondLevel: [],
        listSecondLevelFirst: [],
        nuevoNiif: {},
        itemSelected: {},
        listVigencia: [],
        isLoading: false,
        showBtnCrear: false,
        vigenciaSelected: "",
        selReporte: "",
        listReporte: [],
        showModDel: false,
        rowDelete: null,
    }),
    computed: {
        isVigenciaBloqueada() {
            return this.selVigencia ? !this.$store.getters.isPeriodoEnabled(this.selVigencia) : false
        },
        showToolbarExtras() {
            return this.fgl_consulta && this.listPlanConsoli.length > 0
        },
        showSearchTable() {
            return this.fgl_consulta && this.listPlanConsoli.length > 0
        },
        labels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: "NIIF",
                    selectReport: "Seleccione Reporte",
                    selectValidity: "Seleccione Vigencia",
                    search: "Buscar",
                    create: "Crear",
                    niif: "Código",
                    company: "Compañía",
                    validity: "Vigencia",
                    report: "Reporte",
                    description: "Descripción",
                    levelFirst: "Primer Nivel",
                    levelSecond: "Segundo Nivel",
                    consultar: "Consultar",
                    edit: "Editar",
                    delete: "Eliminar",
                    clear: "Limpiar",
                    showing: "Mostrando",
                    of: "de",
                    records: "registros",
                    noData: "No hay datos disponibles",
                    noResults: "No se encontraron resultados",
                    loading: "Cargando...",
                    noMatches: "No se encontraron resultados",
                    searchPlaceholder: "Buscar en la tabla...",
                    rowsPerPage: "Filas por página:",
                    noSort: "Sin orden",
                    sortAsc: "A-Z / Menor a mayor",
                    sortDesc: "Z-A / Mayor a menor",
                    columns: "Columnas",
                    showAllColumns: "Mostrar todas",
                    filters: "Filtros",
                    clearFilters: "Limpiar filtros",
                    filterNiif: "Filtrar código...",
                    filterDescription: "Filtrar descripción...",
                    filterFirstLevel: "Filtrar primer nivel...",
                    filterSecondLevel: "Filtrar segundo nivel...",
                    selectFilters: "Seleccione los filtros y consulte"
                }
            } else {
                return {
                    title: "NIIF",
                    selectReport: "Select Report",
                    selectValidity: "Select Validity",
                    search: "Search",
                    create: "Create",
                    niif: "Code",
                    company: "Company",
                    validity: "Validity",
                    report: "Report",
                    description: "Description",
                    levelFirst: "Level First",
                    levelSecond: "Level Second",
                    consultar: "Consult",
                    edit: "Edit",
                    delete: "Delete",
                    clear: "Clear",
                    showing: "Showing",
                    of: "of",
                    records: "records",
                    noData: "No data available",
                    noResults: "No results found",
                    loading: "Loading...",
                    noMatches: "No results found",
                    searchPlaceholder: "Search in table...",
                    rowsPerPage: "Rows per page:",
                    noSort: "No sorting",
                    sortAsc: "A-Z / Low to high",
                    sortDesc: "Z-A / High to low",
                    columns: "Columns",
                    showAllColumns: "Show all",
                    filters: "Filters",
                    clearFilters: "Clear filters",
                    filterNiif: "Filter code...",
                    filterDescription: "Filter description...",
                    filterFirstLevel: "Filter first level...",
                    filterSecondLevel: "Filter second level...",
                    selectFilters: "Select filters and consult"
                }
            }
        },
        allFields() {
            return [
                { key: 'niif', label: this.labels.niif, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'nameNiif', label: this.labels.description, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'namePrimerNivel', label: this.labels.levelFirst, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'nameSegundoNivel', label: this.labels.levelSecond, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        tableFilterDefs() {
            return [
                { key: 'niif', type: 'text', placeholder: this.labels.filterNiif },
                { key: 'nameNiif', type: 'text', placeholder: this.labels.filterDescription },
                { key: 'namePrimerNivel', type: 'text', placeholder: this.labels.filterFirstLevel },
                { key: 'nameSegundoNivel', type: 'text', placeholder: this.labels.filterSecondLevel }
            ]
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        }
    },
    watch: {
        selVigencia(valNew) {
            if (valNew !== this.selVigenciaAnterior) {
                this.selVigenciaAnterior = valNew
                if (this.isVigenciaBloqueada) {
                    this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                }
            }
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },

        onReportChange() {
            this.listPlanConsoli = []
            this.fgl_consulta = false
            this.f_lea_primer_nivel()
            this.f_lea_segundo_nivel()
        },

        f_lea_primer_nivel() {
            if (!this.selReporte) return
            PrimerNivel.getFirstLevelByReporte(this.selReporte)
                .then((r_primerNivel) => {
                    this.listFirstLevel = r_primerNivel.data.firstLevByReport || []
                })
                .catch((err) => console.error(err))
        },

        f_lea_segundo_nivel() {
            if (!this.selReporte) return
            SegundoNivel.getAllLevelSecond(this.selReporte)
                .then((r_segundoNivel) => {
                    this.listSecondLevel = r_segundoNivel.data.segundoNivel || []
                })
                .catch((err) => console.error(err))
        },

        crearNiif() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            if (!this.selReporte || !this.selVigencia) {
                this.$toast.error('Seleccione un reporte y una vigencia.')
                return
            }
            
            if (this.listFirstLevel.length === 0) {
                this.f_lea_primer_nivel()
            }
            
            this.showDialogCrearNiif = true
            this.vigenciaSelected = this.selVigencia
        },

        editaNiif(id) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.itemSelected = this.listPlanConsoli.find((d_niif) => d_niif.id === id)
            if (!this.itemSelected) return
            this.itemSelected.descripcion = this.itemSelected.nameNiif
            this.itemSelected.codReporte = this.selReporte
            this.itemSelected.vigencia = this.selVigencia
            this.itemSelected.niif_nuevo = this.itemSelected.niif
            this.listSecondLevelFirst = this.listSecondLevel.filter((d_second) => d_second.primer_nivel == this.itemSelected.primer_nivel)
            this.showDialogEditNiif = true
        },

        leaSegundoNivel(p_primer_nivel) {
            const SecondLevelFirst = this.listSecondLevel.filter((d_second) => d_second.primer_nivel == p_primer_nivel)
            this.listSecondLevelFirst = SecondLevelFirst
        },

        deleteNiif(id) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.rowDelete = id
            this.showModDel = true
        },

        confirmDel() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                this.showModDel = false
                return
            }
            PlanConsolidacion.deletePlanconsolidacion(this.rowDelete)
                .then((res) => {
                    this.$toast.success(res.data.message)
                    this.showModDel = false
                    this.showDialogEditNiif = false
                    this.refrescaTabla()
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al eliminar el registro')
                })
        },

        closeModal(value) {
            this.showDialogEditNiif = value
            this.showDialogCrearNiif = value
            this.showModDel = value
            if (!value) {
                this.rowDelete = null
            }
        },

        consultaNiif() {
            if (!this.selVigencia || !this.selReporte) {
                this.$toast.error('Seleccione un reporte y una vigencia.')
                return
            }
            this.isLoading = true
            PlanConsolidacion.getPlanConsPorCompVige(this.selVigencia, this.selReporte)
                .then((res) => {
                    this.listPlanConsoli = res.data.planConsNiif || []
                    this.listPlanConsoliOrigen = [...this.listPlanConsoli]
                    this.fgl_consulta = true
                })
                .catch((err) => {
                    console.error(err)
                    this.listPlanConsoli = []
                    this.fgl_consulta = true
                    this.$toast.error('Error al consultar los datos')
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        refrescaTabla() {
            this.consultaNiif()
        }
    },
    mounted() {
        this.isLoading = true
        Promise.all([
            PlanConsolidacion.getVigenciaPlanCons(),
            TipoReporte.getAllTipoReporte()
        ])
        .then(([vigenciaRes, reporteRes]) => {
            this.listVigencia = vigenciaRes.data.getVigencia || []
            this.listReporte = reporteRes.data.typeReport || []
        })
        .catch((err) => {
            console.error(err)
            this.$toast.error('Error al cargar los datos iniciales')
        })
        .finally(() => {
            this.isLoading = false
        })
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.niif-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #218838;
    border-color: #1e7e34;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    
    .niif-header .filters-container {
        gap: 4px;
    }
    
    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }
}
</style>